import type { BlocksContentCollection } from '../../grid/reduxStore/editorSlice';
import type { BlockConfig } from '../models/BlockConfig.model';
import { useNewBlockLayerChangeHandler } from './useNewBlockLayerChangeHandler';

type BlockCoordinates = {
  topX: number;
  topY: number;
  bottomX: number;
  bottomY: number;
};
const blocksOverlaps = (firstBlockCoordinates: BlockCoordinates, secondBlockCoordinates: BlockCoordinates): boolean => {
  return (
    firstBlockCoordinates.topX < secondBlockCoordinates.bottomX &&
    firstBlockCoordinates.bottomX > secondBlockCoordinates.topX &&
    firstBlockCoordinates.topY < secondBlockCoordinates.bottomY &&
    firstBlockCoordinates.bottomY > secondBlockCoordinates.topY
  );
};

const getBlockCoordinates = (blockConfig: BlockConfig): BlockCoordinates => {
  const { x: topX, y: topY, width, height } = blockConfig;

  return { topX: topX, topY: topY, bottomX: topX + width, bottomY: topY + height };
};

const isCurrentBlockInNextTargetLayer = (
  currentBlockZIndex: number,
  selectedBlockZIndex: number,
  targetZIndex: number,
  targetLayer: TargetLayer
) => {
  if (targetLayer == TargetLayer.Forward) {
    return currentBlockZIndex > selectedBlockZIndex && currentBlockZIndex < targetZIndex;
  }
  return currentBlockZIndex < selectedBlockZIndex && currentBlockZIndex > targetZIndex;
};
enum TargetOuterLayer {
  Front = -1,
  Back = 1,
}
enum TargetLayer {
  Forward = 1,
  Backward = -1,
}

export default function useBlockLayer() {
  const { changeLayerForBlock, recordTransaction } = useNewBlockLayerChangeHandler();

  const swapBlockLayers = (selectedBlockId: string, targetZIndex: number, targetBlockId: string, selectedBlockZIndex: number) => {
    recordTransaction([
      { blockId: selectedBlockId, zIndex: targetZIndex },
      { blockId: targetBlockId, zIndex: selectedBlockZIndex },
    ]);
  };

  const moveToOuterLayer = (selectedBlockZIndex: number, blockId: string, zIndexAvailable: number, targetOuterLayer: TargetOuterLayer) => {
    const isSelectedBlockInAnOuterLayer = selectedBlockZIndex == zIndexAvailable + targetOuterLayer;
    if (!isSelectedBlockInAnOuterLayer) {
      changeLayerForBlock({
        blockId,
        zIndex: zIndexAvailable,
      });
    }
  };

  const bringGridToFront = (blockConfig: BlockConfig, nextZIndexAvailable: number) => {
    const { z: myZIndex, id: blockId } = blockConfig;
    moveToOuterLayer(myZIndex, blockId, nextZIndexAvailable, TargetOuterLayer.Front);
  };

  const sendGridToBack = (blockConfig: BlockConfig, lowerZIndexAvailable: number) => {
    const { z: myZIndex, id: blockId } = blockConfig;
    moveToOuterLayer(myZIndex, blockId, lowerZIndexAvailable, TargetOuterLayer.Back);
  };

  const moveToLayer = (blockConfig: BlockConfig, allBlocksContent: BlocksContentCollection, targetLayer: TargetLayer) => {
    const { z: selectedBlockZIndex, id: selectedBlockId } = blockConfig;
    const myBlockCoordinates = getBlockCoordinates(blockConfig);

    let targetBlockId: string | null = null;
    let targetZIndex = Infinity * targetLayer;

    for (const currentBlockId in allBlocksContent) {
      if (currentBlockId === selectedBlockId) {
        continue;
      }

      const currentBlockCoordinates = getBlockCoordinates(allBlocksContent[currentBlockId].blockConfig);
      const { z: currentBlockZIndex } = allBlocksContent[currentBlockId].blockConfig;

      if (
        isCurrentBlockInNextTargetLayer(currentBlockZIndex, selectedBlockZIndex, targetZIndex, targetLayer) &&
        blocksOverlaps(myBlockCoordinates, currentBlockCoordinates)
      ) {
        targetZIndex = currentBlockZIndex;
        targetBlockId = currentBlockId;
      }
    }
    if (targetBlockId) {
      swapBlockLayers(selectedBlockId, targetZIndex, targetBlockId, selectedBlockZIndex);
    }
  };

  const bringGridForward = (selectedBlockConfig: BlockConfig, allBlocksContent: BlocksContentCollection) => {
    moveToLayer(selectedBlockConfig, allBlocksContent, TargetLayer.Forward);
  };

  const sendGridBackward = (selectedBlockConfig: BlockConfig, allBlocksContent: BlocksContentCollection) => {
    moveToLayer(selectedBlockConfig, allBlocksContent, TargetLayer.Backward);
  };

  return { bringGridToFront, bringGridForward, sendGridBackward, sendGridToBack };
}
